import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../utils/tokenManager";
import Papa from "papaparse";
import axios from "axios";
import Button from "../../components/common/Button";
import dropFile from "../../assets/dashboard/drop-file.svg";
import supported from "../../assets/dashboard/support.svg";
import excel from "../../assets/dashboard/excel.svg";
import warning from "../../assets/dashboard/warning-icon.svg";
import fail from "../../assets/agent/fail.svg";

const UploadFileModal = ({ open, onClose }) => {
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadResult, setUploadResult] = useState("");
  console.log("uploadResult", uploadResult);
  const [errorModal, setErrorModal] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorSelect, setErrorSelect] = useState(false);

  const { t } = useTranslation();

  const handleBackdropClick = (e) => {
    if (modalRef.current && e.target === modalRef.current) {
      onClose();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();

      // Check if file format is supported (only CSV)
      if (fileExtension !== "csv") {
        setErrorSelect(true);
        return;
      }

      setSelectedFile(file);
      setUploadProgress(0);
      setUploadComplete(false);
      setUploadResult("");
      setUploadError(null);
      setShowConfirmation(false);
      setIsUploading(false);

      const reader = new FileReader();

      // Helper: decode ArrayBuffer trying UTF-8, then Windows-1252, then ISO-8859-1
      const decodeBufferWithFallback = (buffer) => {
        const uint8 = new Uint8Array(buffer);
        const tryDecode = (label) => {
          try {
            return new TextDecoder(label).decode(uint8);
          } catch (_err) {
            return null;
          }
        };

        // First attempt UTF-8
        let text = tryDecode("utf-8");
        if (text && !text.includes("\uFFFD")) return text;

        // Fallbacks that often cover Spanish/Latin characters from Excel/CSV
        text = tryDecode("windows-1252");
        if (text && !text.includes("\uFFFD")) return text;

        text = tryDecode("iso-8859-1");
        return text || "";
      };

      reader.onload = async (event) => {
        const decodedText = decodeBufferWithFallback(event.target.result);
        Papa.parse(decodedText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const cleanedData = results.data.filter((row) =>
              Object.values(row).some((val) => val !== null && val !== "")
            );
            setParsedData(cleanedData);
            console.log("Parsed CSV data:", cleanedData);
            setShowConfirmation(true);
          },
        });
      };

      // Always read as ArrayBuffer to handle encodings safely
      reader.readAsArrayBuffer(file);
    }
  };

  const uploadFile = async (file, parsedData) => {
    setIsUploading(true);
    try {
      const accessToken = await getValidToken();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("data", JSON.stringify(parsedData));

      const response = await axios.post(
        `${process.env.REACT_APP_APIBASEURL}/api/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${accessToken}`,
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          },
        }
      );
      console.log(response);
      setUploadComplete(true);
      setUploadResult({
        uploaded: response.data.success_count || 0,
        total: parsedData.length,
        errors: response.data.failed_count || 0,
        errorRecords: response.data.failed_items || [],
      });
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError(error.response?.data?.message || "Upload failed");
      setErrorModal(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmUpload = () => {
    uploadFile(selectedFile, parsedData);
  };

  const handleCancelUpload = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadComplete(false);
    setUploadResult("");
    setUploadError(null);
    setShowConfirmation(false);
    setIsUploading(false);
    setParsedData([]);
  };

  const handleUploadAreaClick = () => {
    fileInputRef.current?.click();
  };

  const handleModal = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadComplete(false);
    setUploadResult("");
    setUploadError(null);
    setErrorModal(false);
    setErrorSelect(false);
    setParsedData([]);
    setShowConfirmation(false);
    setIsUploading(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (!open) return null;

  return (
    <div
      ref={modalRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 relative flex flex-col items-center">
        <div className="w-full">
          <h2 className="text-lg sm:text-xl font-semibold text-primary mb-4">
            {t("dashboard.uploadContact.uploadFile")}
          </h2>
          <hr className="border-t border-gray-300 mb-4" />
          <div className="text-base sm:text-lg font-medium text-primary mb-6">
            {t("dashboard.uploadContact.addFile")}
          </div>
          <div className="mb-6">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".csv, .pdf, xls, xlsx, xlsm"
              className="hidden"
            />
            {(!selectedFile ||
              (selectedFile && !showConfirmation && !uploadComplete)) && (
              <div
                onClick={handleUploadAreaClick}
                className="border border-dashed border-secondary rounded-xl flex flex-col items-center justify-center py-10 px-4 cursor-pointer hover:bg-blue-50 transition-all min-h-[200px] sm:min-h-[245px]"
              >
                <img
                  src={dropFile}
                  alt="drop file"
                  className="mb-2 w-12 h-12 sm:w-16 sm:h-16"
                />
                <p className="font-semibold text-sm sm:text-lg">
                  <span className="text-secondary underline cursor-pointer">
                    {t("dashboard.uploadContact.browse")}
                  </span>
                </p>
              </div>
            )}

            {selectedFile && showConfirmation && !uploadComplete && (
              <div className="flex flex-col items-center mt-4">
                <div className="border border-dashed border-secondary rounded-xl flex items-center justify-center py-6 px-4 w-full">
                  <img src={excel} alt="excel" className="w-12 h-12 mr-3" />
                  <div>
                    <div className="font-semibold text-primary">
                      {selectedFile.name}
                    </div>
                    <div className="text-xs text-primary mb-2">
                      {formatFileSize(selectedFile.size)}
                    </div>
                  </div>
                </div>

                <div className="bg-tone rounded-md px-4 py-3 w-full flex items-center my-6">
                  <img src={warning} alt="warning" className="w-8 h-8 mr-3" />
                  <div>
                    <span className="text-primary font-semibold flex space-x-1">
                      <p>{parsedData.length}</p>
                      <p>{t("dashboard.uploadContact.recordsUploaded")}</p>
                    </span>
                    <span className="block text-xs text-primary">
                      {uploadResult.errors}{" "}
                      {t("dashboard.uploadContact.reviewData")}
                    </span>
                  </div>
                </div>

                <div className="flex gap-4 flex-wrap justify-center mt-6">
                  <Button
                    variant="secondary"
                    className="w-full sm:w-48 px-4 py-2"
                    onClick={handleCancelUpload}
                    disabled={isUploading}
                  >
                    {t("button.cancel")}
                  </Button>
                  <Button
                    variant="primary"
                    className="w-full sm:w-48 px-4 py-2"
                    onClick={handleConfirmUpload}
                    disabled={isUploading}
                  >
                    {isUploading
                      ? t("dashboard.uploadContact.uploading")
                      : t("dashboard.uploadContact.confirmUpload")}
                  </Button>
                </div>
              </div>
            )}

            {selectedFile && uploadComplete && uploadResult ? (
              <div className="flex flex-col items-center mt-4">
                <div className="border border-dashed border-secondary rounded-xl flex items-center justify-center py-6 px-4 w-full">
                  <img src={excel} alt="excel" className="w-12 h-12 mr-3" />
                  <div>
                    <div className="font-semibold text-primary">
                      {selectedFile.name}
                    </div>
                    <div className="text-xs text-primary mb-4">
                      {formatFileSize(selectedFile.size)}
                    </div>
                  </div>
                </div>
                <div className="bg-tone rounded-md px-4 py-3 w-full flex items-center my-6">
                  <img src={warning} alt="warning" className="w-8 h-8 mr-3" />
                  <div>
                    <span className="text-primary font-semibold">
                      {uploadResult.uploaded}/{uploadResult.total}{" "}
                      {t("dashboard.uploadContact.recordsUploaded")}
                    </span>
                    <span className="block text-xs text-primary">
                      {uploadResult.errors}{" "}
                      {t("dashboard.uploadContact.errorsFound")}
                    </span>
                  </div>
                </div>
                <div className="flex gap-4 flex-wrap justify-center">
                  <Link
                    to="/confirmation"
                    onClick={() => {
                      sessionStorage.setItem(
                        "uploadResults",
                        JSON.stringify({
                          uploaded: uploadResult.uploaded,
                          total: uploadResult.total,
                          errors: uploadResult.errors,
                          errorRecords: uploadResult.errorRecords,
                          allRecords: parsedData,
                        })
                      );
                    }}
                  >
                    <Button
                      variant="primary"
                      className="w-full sm:w-48 px-4 py-2"
                    >
                      {t("continue")}
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              !selectedFile && (
                <div className="flex flex-col items-center gap-2 mt-6">
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <span>
                      {t("dashboard.uploadContact.supportedFiles")}: CSV
                    </span>
                    <img
                      src={supported}
                      alt="supported"
                      className="w-2 h-2 sm:w-5 sm:h-5"
                    />
                  </div>
                  <span className="text-xs sm:text-sm text-primary">
                    {t("dashboard.uploadContact.maximumSize")}: 10MB
                  </span>
                </div>
              )
            )}

            {selectedFile && isUploading && !uploadComplete && (
              <div className="w-full flex items-center mt-5">
                <div className="flex items-center bg-gray-100 rounded-lg px-4 py-2 w-full">
                  <img src={excel} alt="excel" className="w-6 h-6 mr-3" />
                  <div className="flex-1">
                    <div className="font-semibold">{selectedFile.name}</div>
                    <div className="text-xs text-gray-500">
                    {formatFileSize(selectedFile.size)}
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-2 mt-1">
                      <div
                        className="bg-secondary h-2 rounded-full"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="ml-2 text-xs">{uploadProgress}%</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.uploadError.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl w-[455px] text-center">
              {uploadError || t("admin.uploadError.descUpload")}
            </p>
            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
      {errorSelect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.uploadError.selectFileHead")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl w-[455px] text-center">
              {t("admin.uploadError.selectFileDesc")}
            </p>
            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadFileModal;
